import { ItemType, IBaseSubHeaderConfig, ICellType, ISwimlane, ISwimlaneConfig } from "@dhx/ts-diagram";
import { el, sv, VNode } from "@dhx/ts-common/dom";
import { IDataItem } from "@dhx/ts-data";
import { uid } from "@dhx/ts-common/core";
import locale from "../../locales/en";
import { BaseGroup } from "../Groups/BaseGroup";
import {
	getMenuIcon,
	getSwimlaneBackGround,
	getSwimlaneContainerStyle,
	getTextStyle,
} from "../templates/templates";
import { getMeta } from "../../helpers/baseMetaInfo";

export class Swimlane extends BaseGroup implements ISwimlane {
	public config: ISwimlaneConfig;
	private subHeaderNearCells = {
		rows: [],
		cols: [],
	};
	private children = {};

	constructor(config: ISwimlaneConfig, params) {
		super(config, params);
	}
	public getBaseType(): ItemType {
		return "swimlane";
	}
	public getMetaInfo(): { [key: string]: string }[] {
		let meta = [
			{ type: "grid", label: locale.gridStep },
			{ type: "arrangeGroup", label: locale.arrange },
			{ type: "fillGroup", label: locale.fill },
			{ type: "strokeGroup", label: locale.stroke },
		];
		if (this.config.header) {
			const header = [
				{ type: "headerText", label: locale.headerText },
				{ type: "headerStyle", label: locale.headerStyle },
				{ type: "headerFill", label: locale.headerFill },
				{ type: "headerPosition", label: locale.headerPosition },
			];
			if (this.config.header.enable) {
				meta = [...meta, ...header];
			}
			meta.push({ type: "headerEnable", label: locale.headerEnable });
		}
		if (this.config.subHeaderRows) {
			meta.push({ type: "subHeaderRowsEnable", label: locale.subHeaderRowsEnable });
		}
		if (this.config.subHeaderCols) {
			meta.push({ type: "subHeaderColsEnable", label: locale.subHeaderColsEnable });
		}
		return getMeta(meta);
	}
	public isEditable(subheaderId: string): boolean {
		if (subheaderId) {
			const type = this.getSubHeaderType(subheaderId);
			const subHeaders = this.config[type === "col" ? "subHeaderCols" : "subHeaderRows"];
			const headers = subHeaders?.headers;
			const header = headers.find(({ id }) => subheaderId === id);

			if (typeof header?.editable === "boolean") {
				return header.editable;
			}

			return subHeaders?.editable;
		}
		return this.config?.header?.editable;
	}
	public getChildHtmlNodes(config: ISwimlaneConfig, data: IDataItem[]): VNode[] {
		this.removeExtraDefaults(data);
		this.setExtraDefaults(config, data);

		return super.getChildHtmlNodes(config, data);
	}
	public render(childData?: IDataItem[]): VNode {
		const { id, header, open, $width, $height, subHeaderCols, subHeaderRows } = this.config;
		let children = [];

		const data = childData ? childData : this.data.getChildren(this.id, false);
		children = this.getChildHtmlNodes(this.config, data);

		const isOpen = header?.enable && header?.closable ? open : true;

		if (!children.length && isOpen) {
			return;
		}

		this.setServiceProp(this.config);

		const isHeaderEnable = header?.enable;
		const isSubHeaderColsEnable = isOpen && subHeaderCols?.enable;
		const isSubHeaderRowsEnable = isOpen && subHeaderRows?.enable;

		let currentCss = "dhx_diagram_item dhx_diagram_group ";
		if (header?.enable) {
			currentCss += `dhx_diagram_group--position_${header.position}`;
		}

		return el(
			"div.dhx_diagram_swimlane",
			{
				_key: id,
			},
			[
				el(
					"div",
					{
						dhx_id: id,
						class: `${currentCss} ${this.getCss()}`,
						style: getSwimlaneContainerStyle(this.config, this.getCoords(this.config)),
					},
					[
						el("div.dhx_diagram_group__children", [
							sv(
								"svg.dhx_diagram_group__children",
								{
									xmlns: "http://www.w3.org/2000/svg",
									reserveAspectRatio: "none",
									width: $width,
									height: $height,
								},
								this.getChildSvgNodes(this.config, this.data)
							),
							...children,
						]),
						isHeaderEnable && this.getGroupHeader(this.config, this.getEditorNode()),
						isSubHeaderColsEnable && this.getSubHeaderNode(this.config, "cols"),
						isSubHeaderRowsEnable && this.getSubHeaderNode(this.config, "rows"),
					]
				),
				el("div.dhx_diagram_group--background", {
					style: getSwimlaneBackGround(this.config, this.getCoords(this.config)),
				}),
			]
		);
	}
	protected setDefaults(config: ISwimlaneConfig): ISwimlaneConfig {
		this.setServiceProp(config);
		super.setDefaults(config);

		const subHeaderDefault: IBaseSubHeaderConfig = {
			fontSize: 14,
			lineHeight: 14,
			textAlign: "center",
			textVerticalAlign: "center",
			fontStyle: "normal",
			fontWeight: "500",
			fontColor: "#4C4C4C",
			iconColor: "#808080",
			editable: true,
			fill: "#F7F7F7",
		};

		if (config.subHeaderRows) {
			config.subHeaderRows = {
				height: 40,
				enable: true,
				position: "left",

				...subHeaderDefault,
				...config.subHeaderRows,
			};
			if (!Array.isArray(config.subHeaderRows.headers)) {
				config.subHeaderRows.headers = [];
			}
		}

		if (config.subHeaderCols) {
			config.subHeaderCols = {
				height: 40,
				enable: true,
				position: "top",

				...subHeaderDefault,
				...config.subHeaderCols,
			};
			if (!Array.isArray(config.subHeaderCols.headers)) {
				config.subHeaderCols.headers = [];
			}
		}

		return config;
	}
	protected setExtraDefaults(config: ISwimlaneConfig, data: IDataItem[]): void {
		const {
			layout,
			height,
			width,
			header,
			x,
			y,
			subHeaderCols,
			subHeaderRows,
			style: { stroke, strokeWidth },
		} = config;

		let children = {};
		let cellHeight = 0;
		let cellWidth = 0;

		const {
			isHeaderBottom,
			isHeaderLeft,
			isHeaderRight,
			isHeaderTop,
			isSubHeaderBottom,
			isSubHeaderLeft,
			isSubHeaderRight,
			isSubHeaderTop,
		} = this.getHeaderStates(config);

		const headerOffset = parseFloat(header?.height as string) || 0;
		const subHeaderRowsOffset = parseFloat(subHeaderRows?.height as string) || 0;
		const subHeaderColsOffset = parseFloat(subHeaderCols?.height as string) || 0;

		if (height) {
			let offset = 0;
			if (isHeaderTop || isHeaderBottom) offset += headerOffset;
			if (isSubHeaderTop || isSubHeaderBottom) offset += subHeaderColsOffset;

			cellHeight = (height - offset) / layout.length;
		}
		if (width) {
			let offset = 0;
			if (isHeaderRight || isHeaderLeft) offset += headerOffset;
			if (isSubHeaderRight || isSubHeaderLeft) offset += subHeaderRowsOffset;

			let maxLength = 0;

			for (let index = 0; index < layout.length; index++) {
				const length = layout[index].length;
				if (length > maxLength) maxLength = length;
			}
			cellWidth = (width - offset) / maxLength;
		}

		this.subHeaderNearCells = { rows: [], cols: [] };
		this.children = {};

		for (let i = 0; i < data.length; i++) {
			this.children[data[i].id] = data[i];
		}

		for (let i = 0; i < layout.length; i++) {
			const row = layout[i];
			for (let k = 0; k < row.length; k++) {
				const cell = row[k];

				const item = children[cell];
				const prevItem = k !== 0 && children[row[k - 1]];
				const overItem = i !== 0 && children[layout[i - 1][k]];

				if (!item && this.children[cell]) {
					children[cell] = this.children[cell];

					children[cell].height = cellHeight;
					children[cell].width = cellWidth;

					children[cell].$rowCount = 1;
					children[cell].$colCount = 1;

					if (k === 0) {
						let offset = 0;
						if (isHeaderLeft) offset += headerOffset;
						if (isSubHeaderLeft) offset += subHeaderRowsOffset;

						children[cell].x = x + offset;

						this.subHeaderNearCells.rows.push(children[cell]);
					} else {
						children[cell].x = prevItem.x + prevItem.width;
					}

					if (i === 0) {
						let offset = 0;
						if (isHeaderTop) offset += headerOffset;
						if (isSubHeaderTop) offset += subHeaderColsOffset;

						children[cell].y = y + offset;

						this.subHeaderNearCells.cols.push(children[cell]);
					} else {
						children[cell].y = overItem.y + overItem.height;
					}

					const rowLength = this.subHeaderNearCells.rows.length - 1;
					if (config?.subHeaderRows?.enable) {
						if (!config.subHeaderRows?.headers[rowLength]) {
							config.subHeaderRows.headers[rowLength] = {
								text: "Untitled",
								id: uid(),
							};
						}
						if (!config.subHeaderRows?.headers[rowLength]?.id) {
							config.subHeaderRows.headers[rowLength].id = uid();
						}
					}

					const colLength = this.subHeaderNearCells.cols.length - 1;
					if (config?.subHeaderCols?.enable) {
						if (!config.subHeaderCols?.headers[colLength]) {
							config.subHeaderCols.headers[colLength] = {
								text: "Untitled",
								id: uid(),
							};
						}
						if (!config.subHeaderCols?.headers[colLength]?.id) {
							config.subHeaderCols.headers[colLength].id = uid();
						}
					}

					children[cell].style.stroke = stroke;
					children[cell].style.strokeWidth = strokeWidth;
				} else if (item) {
					if (prevItem === item && overItem !== item) {
						children[cell].width += cellWidth;
						children[cell].$colCount++;
					}
					if (overItem === item && prevItem !== item) {
						children[cell].height += cellHeight;
						children[cell].$rowCount++;
					}
				}

				if (prevItem && prevItem !== item) {
					children[cell].$borderPosition.left = true;
				}
				if (overItem && overItem !== item) {
					children[cell].$borderPosition.top = true;
				}
			}
		}

		children = null;
	}
	protected removeExtraDefaults(data: IDataItem[]): void {
		for (let index = 0; index < data.length; index++) {
			data[index].$borderPosition = {
				left: false,
				top: false,
			};
		}
	}
	private getSubHeaderNode(config: ISwimlaneConfig, type: "cols" | "rows"): VNode {
		const { header, subHeaderCols, subHeaderRows, height: $height, width: $width, id } = config;

		const {
			isHeaderBottom,
			isHeaderLeft,
			isHeaderRight,
			isHeaderTop,
			isSubHeaderBottom,
			isSubHeaderLeft,
			isSubHeaderRight,
			isSubHeaderTop,
		} = this.getHeaderStates(config);

		const headerOffset = parseFloat(header?.height as string) || 0;
		const subHeaderRowsOffset = parseFloat(subHeaderRows?.height as string) || 0;
		const subHeaderColsOffset = parseFloat(subHeaderCols?.height as string) || 0;

		let width = 0;
		let height = 0;
		let x = 0;
		let y = 0;

		if (type === "cols") {
			height = subHeaderColsOffset;
			width = $width;

			if (isSubHeaderTop && isHeaderTop) {
				y = headerOffset;
			}
			if (isSubHeaderBottom) {
				y = $height - subHeaderColsOffset;

				if (isHeaderBottom) {
					y = $height - headerOffset - subHeaderColsOffset;
				}
			}
			if (isHeaderLeft) {
				x = headerOffset;
				width = $width - headerOffset;
			}
			if (isHeaderRight) {
				width = $width - headerOffset;
			}
		}

		if (type === "rows") {
			height = $height;
			width = subHeaderRowsOffset;

			if (isSubHeaderLeft && isHeaderLeft) {
				x = headerOffset;
			}
			if (isSubHeaderRight) {
				x = $width - subHeaderRowsOffset;

				if (isHeaderRight) {
					x = $width - subHeaderRowsOffset - headerOffset;
				}
			}
			if (isHeaderTop) {
				y = headerOffset;
				height = $height - headerOffset;
			}
			if (isHeaderBottom) {
				height = $height - headerOffset;
			}
			if (isSubHeaderTop) {
				y += subHeaderColsOffset;
				height -= subHeaderColsOffset;
			}
			if (isSubHeaderBottom) {
				height -= subHeaderColsOffset;
			}
		}

		const style: any = {
			height,
			width,
			top: y,
			left: x,
		};

		const headersLength = this.subHeaderNearCells[type].length;
		const headers = type === "cols" ? subHeaderCols.headers : subHeaderRows.headers;
		const cellsNode = [];

		for (let index = 0; index < headersLength; index++) {
			const cell = headers[index];

			const headerHeight = type === "rows" ? this.subHeaderNearCells.rows[index]?.height : "100%";
			let headerWidth: number | string = "100%";

			cell.$subMenu = {
				enable: true,
				data: [],
			};

			if (cell.$subMenu.enable) {
				const menuItemId = `${id}/${cell.id}/`;

				if (type === "cols") {
					cell.$subMenu.data = [
						{
							id: menuItemId + "add_column_left",
							value: locale.menuAddColumnLeft,
							icon: "dxi dxi-table-column-plus-before",
							add: "left",
						},
						{
							id: menuItemId + "add_column_right",
							value: locale.menuAddColumnRight,
							icon: "dxi dxi-table-column-plus-after",
							add: "right",
						},
						{
							id: menuItemId + "move_column_left",
							value: locale.menuMoveColumnLeft,
							icon: "dxi dxi-arrow-left",
							disabled: index === 0,
							move: "left",
						},
						{
							id: menuItemId + "move_column_right",
							value: locale.menuMoveColumnRight,
							icon: "dxi dxi-arrow-right",
							disabled: index === headersLength - 1,
							move: "right",
						},
						{
							type: "separator",
						},
						{
							id: menuItemId + "remove",
							value: locale.menuDeleteCol,
							icon: "dxi dxi-delete-forever",
							disabled: headersLength === 1,
							remove: "col",
						},
					];
				}
				if (type === "rows") {
					cell.$subMenu.data = [
						{
							id: menuItemId + "add_row_up",
							value: locale.menuAddRowUp,
							icon: "dxi dxi-table-row-plus-before",
							add: "up",
						},
						{
							id: menuItemId + "add_row_down",
							value: locale.menuAddRowDown,
							icon: "dxi dxi-table-row-plus-after",
							add: "down",
						},
						{
							id: menuItemId + "move_row_up",
							value: locale.menuMoveRowUp,
							icon: "dxi dxi-arrow-up",
							disabled: index === 0,
							move: "up",
						},
						{
							id: menuItemId + "move_row_down",
							value: locale.menuMoveRowDown,
							icon: "dxi dxi-arrow-down",
							disabled: index === headersLength - 1,
							move: "down",
						},
						{
							type: "separator",
						},
						{
							id: menuItemId + "remove",
							value: locale.menuDeleteRow,
							icon: "dxi dxi-delete-forever",
							disabled: headersLength === 1,
							remove: "row",
						},
					];
				}
			}

			if (type === "cols") {
				headerWidth = this.subHeaderNearCells.cols[index]?.width;
				if ((isSubHeaderLeft && index === 0) || (isSubHeaderRight && index === headersLength - 1)) {
					(headerWidth as number) += subHeaderRowsOffset;
				}
			}

			cellsNode.push(
				this.getSubHeaderCellNode(
					{
						...cell,
						type,
						index,
						width: headerWidth,
						height: headerHeight,
					},
					this.config,
					this.getEditorNode()
				)
			);
		}

		return el(
			`div.dhx_diagram_swimlane__subheader.dhx_diagram_swimlane__subheader--position_${type}`,
			{
				style,
			},
			[...cellsNode]
		);
	}
	private getSubHeaderCellNode(cell, config: ISwimlaneConfig, editorNode: VNode): VNode {
		const { isSubHeaderLeft, isSubHeaderTop } = this.getHeaderStates(config);
		const { strokeWidth, stroke } = config.style;
		const { width, height, id, text, type, index } = cell;

		const {
			lineHeight,
			textAlign,
			textVerticalAlign,
			fontSize,
			fontColor,
			fontStyle,
			fontWeight,
			fill,
			iconColor,
		} = type === "cols" ? config.subHeaderCols : config.subHeaderRows;

		const textConfig = {
			lineHeight: cell.lineHeight || lineHeight,
			textAlign: cell.textAlign || textAlign,
			textVerticalAlign: cell.textVerticalAlign || textVerticalAlign,
			fontSize: cell.fontSize || fontSize,
			fontColor: cell.fontColor || fontColor,
			fontStyle: cell.fontStyle || fontStyle,
			fontWeight: cell.fontWeight || fontWeight,
		};

		const style: any = {
			width,
			height,
		};

		if (type === "cols") {
			const borderLeft = index !== 0 ? `, ${strokeWidth}px 0px 0px ${stroke} inset` : "";

			style.boxShadow = `0px ${
				isSubHeaderTop ? "-" : ""
			}${strokeWidth}px 0px ${stroke} inset ${borderLeft}`;
		}
		if (type === "rows") {
			const borderTop = index !== 0 ? `, 0px ${strokeWidth}px 0px ${stroke} inset` : "";

			style.boxShadow = `${
				isSubHeaderLeft ? "-" : ""
			}${strokeWidth}px 0px 0px ${stroke} inset ${borderTop}`;
		}

		return el(
			"div.dhx_diagram_swimlane__subheader_container",
			{
				_key: id,
				subheader_id: id,
				style,
			},
			[
				el(
					"div.dhx_diagram_swimlane__subheader_text",
					{
						style: {
							...getTextStyle({ ...textConfig, container: { width, height } }),
						},
					},
					config.$editable && this.editableProperty.editableId === id ? [editorNode] : text
				),
				cell.$subMenu.enable && getMenuIcon({ dir: type, color: cell.iconColor || iconColor }),
				el("div", {
					style: {
						position: "absolute",
						width,
						height,
						background: cell.fill || fill,
						zIndex: -1,
					},
				}),
			]
		);
	}
	private getHeaderStates({ header, subHeaderRows, subHeaderCols }: ISwimlaneConfig) {
		const isHeaderTop = header?.enable && header?.position === "top";
		const isHeaderBottom = header?.enable && header?.position === "bottom";
		const isHeaderRight = header?.enable && header?.position === "right";
		const isHeaderLeft = header?.enable && header?.position === "left";

		const isSubHeaderTop = subHeaderCols?.enable && subHeaderCols?.position === "top";
		const isSubHeaderBottom = subHeaderCols?.enable && subHeaderCols?.position === "bottom";
		const isSubHeaderRight = subHeaderRows?.enable && subHeaderRows?.position === "right";
		const isSubHeaderLeft = subHeaderRows?.enable && subHeaderRows?.position === "left";

		return {
			isHeaderTop,
			isHeaderBottom,
			isHeaderRight,
			isHeaderLeft,
			isSubHeaderTop,
			isSubHeaderBottom,
			isSubHeaderRight,
			isSubHeaderLeft,
		};
	}
	private getSubHeaderType(subheaderId: string): ICellType | undefined {
		const { subHeaderCols, subHeaderRows } = this.config;
		const isSubHeaderRowEnable = subHeaderRows?.enable && subHeaderRows?.headers?.length;
		const isSubHeaderColEnable = subHeaderCols?.enable && subHeaderCols?.headers.length;

		let rowIndex = -1;
		let colIndex = -1;
		let type: "row" | "col";

		if (isSubHeaderRowEnable) {
			rowIndex = subHeaderRows.headers.findIndex(({ id }) => subheaderId === id);
			if (rowIndex !== -1) type = "row";
		}
		if (isSubHeaderColEnable) {
			colIndex = subHeaderCols.headers.findIndex(({ id }) => subheaderId === id);
			if (colIndex !== -1) type = "col";
		}

		return type;
	}
}
